import type { HistoryEntry } from '@core/entities/History';
import type { HistoryListParams, HistoryRepository } from '@core/repositories/HistoryRepository';
import { httpClient } from '@infrastructure/http/httpClient';
import { toAppError } from '@infrastructure/http/errorMapper';
import { toHistoryEntry, type HistoryResponse } from '@infrastructure/mappers/toHistoryEntry';
import type { PaginatedResponse } from '@shared/types';

interface LaravelPaginatedResponse<T> {
  data: T[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export class HistoryRepositoryImpl implements HistoryRepository {
  async list(
    workspaceId: string,
    params?: HistoryListParams,
  ): Promise<PaginatedResponse<HistoryEntry>> {
    try {
      const response = await httpClient.get<LaravelPaginatedResponse<HistoryResponse>>(
        `/workspaces/${workspaceId}/history`,
        {
          params: {
            search: params?.search || undefined,
            method: params?.method || undefined,
            favorites_only: params?.favoritesOnly ? 1 : undefined,
            page: params?.page,
            per_page: params?.perPage,
          },
        },
      );

      return {
        data: response.data.data.map(toHistoryEntry),
        meta: {
          currentPage: response.data.meta.current_page,
          lastPage: response.data.meta.last_page,
          perPage: response.data.meta.per_page,
          total: response.data.meta.total,
        },
      };
    } catch (error) {
      throw toAppError(error);
    }
  }

  async get(id: string): Promise<HistoryEntry> {
    try {
      const response = await httpClient.get<{ data: HistoryResponse }>(`/history/${id}`);
      return toHistoryEntry(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async remove(id: string): Promise<void> {
    try {
      await httpClient.delete(`/history/${id}`);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async clear(workspaceId: string): Promise<void> {
    try {
      await httpClient.delete(`/workspaces/${workspaceId}/history`);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async favorite(id: string): Promise<void> {
    try {
      await httpClient.post(`/history/${id}/favorite`);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async unfavorite(id: string): Promise<void> {
    try {
      await httpClient.delete(`/history/${id}/favorite`);
    } catch (error) {
      throw toAppError(error);
    }
  }
}
